import React, { useEffect, useState } from 'react';
import { useSEO } from '../lib/useSEO';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { boardsAPI } from '../lib/api';
import { toast } from 'sonner';
import { Plus, LayoutGrid, Play, Edit, Trash2, ArrowLeft } from 'lucide-react';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_trivia-saas/artifacts/9y2lje2h_Jep_Prep_Logo.png';

export default function BoardsList() {
  useSEO('My Boards | JepPrep', 'Manage your custom trivia boards. Create, play, and share Jeopardy-style boards.');
  const navigate = useNavigate();
  const [boards, setBoards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    loadBoards();
  }, [page]);

  const loadBoards = async () => {
    try {
      const response = await boardsAPI.list({ page, limit: 12 });
      setBoards(response.data.boards || []);
      setTotalPages(response.data.pages || 1);
    } catch (error) {
      toast.error('Failed to load boards');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (boardId, e) => {
    e.preventDefault();
    e.stopPropagation();
    if (window.confirm('Are you sure you want to delete this board?')) {
      try {
        await boardsAPI.delete(boardId);
        toast.success('Board deleted');
        loadBoards();
      } catch (error) {
        toast.error('Failed to delete board');
      }
    }
  };

  const startGame = (boardId, e) => {
    e.preventDefault();
    e.stopPropagation();
    navigate(`/boards/${boardId}/play`);
  };

  return (
    <div className="min-h-screen bg-[hsl(var(--background))]">
      {/* Navigation */}
      <nav className="border-b border-[hsl(var(--border))] bg-[hsl(var(--secondary))]">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex h-16 items-center justify-between">
            <div className="flex items-center gap-2 sm:gap-4 min-w-0">
              <Link to="/dashboard">
                <Button variant="ghost" size="icon" className="text-gray-300 shrink-0">
                  <ArrowLeft className="w-4 h-4" />
                </Button>
              </Link>
              <img src={LOGO_URL} alt="JepPrep" className="h-12 shrink-0" />
            </div>
            <Link to="/boards/new">
              <Button className="bg-[hsl(var(--brand-gold-500))] text-[hsl(var(--brand-blue-900))] hover:bg-[hsl(var(--brand-blue-800))] hover:text-white text-sm shrink-0" data-testid="create-board-button">
                <Plus className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Create Board</span>
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-6">
          <h1 className="font-display font-bold text-2xl text-white flex items-center gap-2">
            <LayoutGrid className="w-6 h-6 text-[hsl(var(--brand-gold-500))]" />
            My Boards
          </h1>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="text-gray-400">Loading boards...</div>
          </div>
        ) : boards.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {boards.map((board) => (
              <Card key={board.id} className="bg-[hsl(var(--secondary))] border-[hsl(var(--border))] card-hover" data-testid="board-card">
                <CardContent className="p-4">
                  <h3 className="font-semibold text-lg text-white mb-2 truncate">{board.title}</h3>
                  <p className="text-gray-400 text-sm mb-3">
                    {board.difficulty} • {board.categories?.length || 0} categories
                  </p>
                  <div className="flex items-center justify-between text-xs text-gray-500 mb-4">
                    <span>{board.visibility}</span>
                    <span className={`${(board.play_count || 0) >= 3 ? 'text-orange-400' : ''}`}>
                      {(board.play_count || 0)}/3 plays
                    </span>
                    <span>{new Date(board.created_at).toLocaleDateString()}</span>
                  </div>
                  <div className="flex gap-2">
                    <Button 
                      onClick={(e) => startGame(board.id, e)} 
                      size="sm" 
                      className={`flex-1 ${(board.play_count || 0) >= 3 ? 'bg-orange-500/20 text-orange-400 hover:bg-orange-500/30' : 'bg-[hsl(var(--brand-gold-500))] text-[hsl(var(--brand-blue-900))] hover:bg-[hsl(var(--brand-blue-800))] hover:text-white'}`}
                      disabled={!board.categories || board.categories.length < 6}
                      data-testid="play-board-button"
                    >
                      <Play className="w-3 h-3 mr-1" />
                      {(board.play_count || 0) >= 3 ? 'Expired' : 'Play'}
                    </Button>
                    <Link to={`/boards/${board.id}/edit`} className="flex-1">
                      <Button variant="outline" size="sm" className="w-full border-[hsl(var(--border))] text-gray-300">
                        <Edit className="w-3 h-3 mr-1" />
                        Edit
                      </Button>
                    </Link>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={(e) => handleDelete(board.id, e)}
                      className="text-red-400 hover:text-red-300"
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card className="bg-[hsl(var(--secondary))] border-[hsl(var(--border))]">
            <CardContent className="p-12 text-center">
              <LayoutGrid className="w-16 h-16 mx-auto text-gray-600 mb-4" />
              <h3 className="text-xl text-white mb-2">No boards yet</h3>
              <p className="text-gray-400 mb-6">Create your first board to start playing!</p>
              <Link to="/boards/new">
                <Button className="bg-[hsl(var(--brand-gold-500))] text-[hsl(var(--brand-blue-900))] hover:bg-[hsl(var(--brand-blue-800))] hover:text-white">
                  <Plus className="w-4 h-4 mr-2" />
                  Create Board
                </Button>
              </Link>
            </CardContent>
          </Card>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-center gap-2 mt-8">
            <Button
              variant="outline"
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="border-[hsl(var(--border))] text-gray-300"
            >
              Previous
            </Button>
            <span className="flex items-center px-4 text-gray-400">
              Page {page} of {totalPages}
            </span>
            <Button
              variant="outline"
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="border-[hsl(var(--border))] text-gray-300"
            >
              Next
            </Button>
          </div>
        )}
      </main>
    </div>
  );
}
